class InstagramReelsSlider extends HTMLElement {
  connectedCallback() {
    this.autoscrollEnabled = this.dataset.autoscroll !== 'false';
    this.direction = this.dataset.direction === 'right' ? -1 : 1;
    this.duration = parseFloat(this.dataset.duration) || 60;

    this.viewport = this.querySelector('.ig-reels__viewport');
    this.track = this.querySelector('.ig-reels__track');
    this.cards = Array.from(
      this.querySelectorAll('.ig-reel-card:not(.ig-reel-card--placeholder)')
    );

    this.hasHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    this.isMobile = window.matchMedia('(max-width: 749px)').matches;
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.isPaused = false;
    this.isDragging = false;
    this.justDragged = false;
    this.jumpDistance = 0;
    // Mute state is per-video. Each card's <video muted> attribute holds
    // its own muted flag and the mute button toggles that specific video
    // — unmuting one reel no longer leaks sound into the others.

    if (!this.viewport || !this.track) return;

    this.setupVideoInteractions();
    this.setupCenterTracking();
    this.setupDragToScroll();
    this.setupClickSuppression();
    this.setupShopDrawer();
    this.setupMuteButton();
    this.setupTracking();

    if (this.autoscrollEnabled && !this.isMobile && !this.prefersReducedMotion) {
      this.setupAutoscroll();
    } else {
      // Non-autoscroll paths still need centering + initial play
      requestAnimationFrame(() => {
        this.initializeCenter();
        this.updateCenterPlayback({ initial: true });
      });
    }
  }

  disconnectedCallback() {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.onDocumentClick) document.removeEventListener('click', this.onDocumentClick);
    if (this.onWindowMouseMove) window.removeEventListener('mousemove', this.onWindowMouseMove);
    if (this.onWindowMouseUp) window.removeEventListener('mouseup', this.onWindowMouseUp);
  }

  /* ---------- Video play / in-view tracking ----------

     Invariants enforced:
     1. At most one video plays at a time (`this.currentVideo`).
     2. A video only plays if its card is >=50% visible in the viewport.
     3. Any playing video that scrolls out of view is paused immediately.
     4. On desktop, hover triggers play (bounded by 1 & 2).
     5. On touch, the most-visible card auto-plays (bounded by 1 & 2).
  ---------------------------------------------------- */

  setupVideoInteractions() {
    this.currentVideo = null;
    this.inViewCards = new Set();
    this.cardRatios = new Map();
    this.lastCenterCard = null;
    this.hoveredCard = null;

    this.setupInViewObserver();

    if (this.hasHover) {
      this.setupHoverPlay();
    }
  }

  setupInViewObserver() {
    if (!('IntersectionObserver' in window)) {
      // Fallback: treat every card as in view
      this.cards.forEach((c) => this.inViewCards.add(c));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const card = entry.target;
          this.cardRatios.set(card, entry.intersectionRatio);

          if (entry.intersectionRatio >= 0.5) {
            this.inViewCards.add(card);
          } else {
            this.inViewCards.delete(card);
            // Playing video scrolled out → pause it
            const video = card.querySelector('video');
            if (video && this.currentVideo === video) {
              this.pauseVideo(video);
            }
          }
        });
      },
      { root: this.viewport, threshold: 0.5 }
    );

    // Observe every card including clones — clones are visually real on
    // desktop during autoscroll and need their own in-view state so hover
    // play works when the cursor is over a clone card.
    this.cards.forEach((card) => observer.observe(card));
  }

  setupHoverPlay() {
    this.cards.forEach((card) => {
      const video = card.querySelector('video');
      if (!video) return;

      const onLeave = () => {
        if (this.hoveredCard === card) this.hoveredCard = null;

        // Don't interrupt the card that center-playback would play next.
        // If the user is leaving the currently-centered card, keep it
        // playing — otherwise autoscroll flow is broken every time the
        // cursor grazes over it.
        const centerCard = this.getCenterCard();
        if (centerCard !== card) {
          this.pauseVideo(video);
          this.updateCenterPlayback();
        }
      };

      card.addEventListener('mouseenter', () => {
        if (this.isDragging) return;
        this.hoveredCard = card;
        this.playVideo(video, card);
      });
      card.addEventListener('mouseleave', onLeave);
      card.addEventListener('focusin', () => {
        this.hoveredCard = card;
        this.playVideo(video, card);
      });
      card.addEventListener('focusout', (event) => {
        if (!card.contains(event.relatedTarget)) onLeave();
      });
    });
  }

  /* ---------- Center-based playback ---------- */

  setupCenterTracking() {
    let pending = false;
    const onScroll = () => {
      if (pending) return;
      pending = true;
      requestAnimationFrame(() => {
        pending = false;
        this.updateCenterPlayback();
      });
    };
    this.viewport.addEventListener('scroll', onScroll, { passive: true });
  }

  /* Position the scroll so that the FIRST clone of card 1 is centered in
     the viewport. Using the clone (instead of the original) means we have
     room to scroll backward — the cards visible to the left of card 1 are
     cards 7 and 8 from copy 1, which is exactly what a loop should look
     like from the user's perspective. */
  initializeCenter() {
    const clone = this.querySelector('.ig-reel-card--clone');
    const target = clone || this.cards[0];
    if (!target) return;

    const centerX = target.offsetLeft + target.offsetWidth / 2;
    const targetScroll = centerX - this.viewport.clientWidth / 2;
    this.viewport.scrollLeft = Math.max(0, targetScroll);
  }

  getCenterCard() {
    const viewportCenter =
      this.viewport.scrollLeft + this.viewport.clientWidth / 2;
    let closest = null;
    let minDist = Infinity;
    for (const card of this.cards) {
      if (card.classList.contains('ig-reel-card--placeholder')) continue;
      // Skip hidden clones on mobile (display: none → offsetWidth 0)
      if (card.offsetWidth === 0) continue;
      const cardCenter = card.offsetLeft + card.offsetWidth / 2;
      const dist = Math.abs(cardCenter - viewportCenter);
      if (dist < minDist) {
        minDist = dist;
        closest = card;
      }
    }
    return closest;
  }

  updateCenterPlayback(options = {}) {
    // Hover takes priority — if the user is actively hovering a card,
    // don't override with center-based play.
    if (this.hoveredCard && !options.initial) return;

    const centerCard = this.getCenterCard();
    if (!centerCard) return;
    if (centerCard === this.lastCenterCard && !options.initial) return;

    this.lastCenterCard = centerCard;
    const video = centerCard.querySelector('video');
    if (!video) return;

    // Initial autoplay must be muted — browser autoplay policies allow
    // muted autoplay unconditionally, but unmuted autoplay requires a
    // user gesture. After the user clicks anywhere (shop, mute, product),
    // subsequent plays will respect `this.isUnmuted` normally.
    this.playVideo(video, centerCard, { forceMuted: options.initial });
  }

  playVideo(video, card, options = {}) {
    if (!video) return;

    // Enforce "only in view" — if we have intersection data, require the card
    // to be flagged as in view. Skip the check only if IO isn't available.
    if (card && this.inViewCards.size > 0 && !this.inViewCards.has(card)) {
      return;
    }

    // Enforce "only one at a time" — and preserve currentTime across the
    // seamless-loop wrap (when switching between an original card and its
    // clone, both hold the same .mp4 so we keep playback continuous).
    if (this.currentVideo && this.currentVideo !== video) {
      const prev = this.currentVideo;
      const prevSrc = this.getVideoSrc(prev);
      const nextSrc = this.getVideoSrc(video);
      const sameSource = prevSrc && prevSrc === nextSrc;

      prev.pause();
      if (sameSource) {
        try { video.currentTime = prev.currentTime; } catch (e) {}
      } else {
        try { prev.currentTime = 0; } catch (e) {}
      }
    }

    this.currentVideo = video;

    // Force mute only on the very first autoplay, which happens before any
    // user gesture. After that, each video keeps its own muted flag — we
    // don't touch it here so per-card mute state is preserved.
    if (options.forceMuted) video.muted = true;

    const p = video.play();
    if (p && typeof p.catch === 'function') {
      p.catch(() => {
        // Autoplay policy blocked unmuted play — retry muted.
        video.muted = true;
        const retry = video.play();
        if (retry && typeof retry.catch === 'function') retry.catch(() => {});
      });
    }
  }

  pauseVideo(video) {
    if (!video) return;
    if (this.currentVideo === video) this.currentVideo = null;
    video.pause();
  }

  getVideoSrc(video) {
    if (!video) return '';
    if (video.currentSrc) return video.currentSrc;
    const source = video.querySelector('source');
    return source ? source.getAttribute('src') || '' : '';
  }

  /* ---------- Autoscroll loop (JS-driven so it composes with drag) ---------- */

  setupAutoscroll() {
    const computeJump = () => {
      const clone = this.querySelector('.ig-reel-card--clone');
      if (clone) {
        this.jumpDistance = clone.offsetLeft - this.cards[0].offsetLeft;
      } else {
        this.jumpDistance = this.track.scrollWidth / 2;
      }
    };

    // Measure after layout settles, then center card 1 and start playing it
    requestAnimationFrame(() => {
      computeJump();
      this.initializeCenter();
      this.updateCenterPlayback({ initial: true });
    });

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(computeJump);
      this.resizeObserver.observe(this.track);
    } else {
      window.addEventListener('resize', computeJump);
    }

    // Hover pauses the autoscroll (not the video, which keeps playing while hovered)
    this.viewport.addEventListener('mouseenter', () => { this.isPaused = true; });
    this.viewport.addEventListener('mouseleave', () => { this.isPaused = false; });

    let lastTs = 0;
    let rafId = null;
    let isOnScreen = true;

    const tick = (ts) => {
      const running =
        !this.isPaused &&
        !this.isDragging &&
        !this.hasOpenDrawer() &&
        this.jumpDistance > 0;

      if (running) {
        if (lastTs) {
          const dt = (ts - lastTs) / 1000;
          const pxPerSec = this.jumpDistance / this.duration;
          let next = this.viewport.scrollLeft + pxPerSec * dt * this.direction;

          if (next >= this.jumpDistance) next -= this.jumpDistance;
          else if (next < 0) next += this.jumpDistance;

          this.viewport.scrollLeft = next;
        }
        lastTs = ts;
      } else {
        lastTs = 0;
      }
      rafId = isOnScreen ? requestAnimationFrame(tick) : null;
    };

    const start = () => {
      if (rafId == null) {
        lastTs = 0;
        rafId = requestAnimationFrame(tick);
      }
    };
    const stop = () => {
      if (rafId != null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    };

    if ('IntersectionObserver' in window) {
      const onScreenObserver = new IntersectionObserver(
        ([entry]) => {
          isOnScreen = entry.isIntersecting;
          if (isOnScreen) start();
          else stop();
        },
        { threshold: 0 }
      );
      onScreenObserver.observe(this);
    } else {
      start();
    }

    this.rafId = rafId;
  }

  /* ---------- Mouse drag to scroll ---------- */

  setupDragToScroll() {
    let isDown = false;
    let startX = 0;
    let startScroll = 0;
    let moved = 0;

    const down = (e) => {
      if (e.button !== 0) return;
      // Don't hijack drags that start on interactive controls or the open drawer
      if (
        e.target.closest(
          '[data-ig-shop-button], [data-ig-mute-button], .ig-reel-card__products'
        )
      ) {
        return;
      }

      // Prevents the browser's native link/image drag from kicking in —
      // without this, mousemove never fires cleanly and the carousel is
      // stuck because the browser is trying to drag the <a>/<img>/<video>.
      e.preventDefault();

      isDown = true;
      this.isDragging = true;
      startX = e.pageX;
      startScroll = this.viewport.scrollLeft;
      moved = 0;
      this.viewport.classList.add('is-dragging');
    };

    const move = (e) => {
      if (!isDown) return;
      const dx = e.pageX - startX;
      const absDx = Math.abs(dx);
      if (absDx > moved) moved = absDx;
      this.viewport.scrollLeft = startScroll - dx;
      if (moved > 4) e.preventDefault();
    };

    const up = () => {
      if (!isDown) return;
      isDown = false;
      this.viewport.classList.remove('is-dragging');

      if (moved > 4) {
        this.justDragged = true;
        setTimeout(() => { this.justDragged = false; }, 60);
      }
      setTimeout(() => {
        this.isDragging = false;
        // Resolve playback after drag: hover wins if still hovering a card,
        // otherwise fall back to the centered card.
        if (this.hoveredCard) {
          const v = this.hoveredCard.querySelector('video');
          if (v) this.playVideo(v, this.hoveredCard);
        } else {
          this.updateCenterPlayback();
        }
      }, 80);
    };

    this.viewport.addEventListener('mousedown', down);
    this.onWindowMouseMove = move;
    this.onWindowMouseUp = up;
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  }

  setupClickSuppression() {
    this.viewport.addEventListener(
      'click',
      (e) => {
        if (this.justDragged) {
          e.preventDefault();
          e.stopPropagation();
        }
      },
      true
    );
  }

  /* ---------- Shop drawer (shoppable products) ---------- */

  setupShopDrawer() {
    const buttons = this.querySelectorAll('[data-ig-shop-button]');
    const closers = this.querySelectorAll('[data-ig-shop-close]');

    buttons.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (this.justDragged) return;

        const card = btn.closest('.ig-reel-card');
        if (!card) return;
        const wasOpen = card.classList.contains('is-chips-open');
        this.closeAllDrawers();
        if (!wasOpen) card.classList.add('is-chips-open');
      });
    });

    closers.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const card = btn.closest('.ig-reel-card');
        if (card) card.classList.remove('is-chips-open');
      });
    });

    this.onDocumentClick = (e) => {
      // Click inside an open drawer (e.g. on a chip link) → let it through
      if (e.target.closest('.ig-reel-card__products')) return;
      // Click on a shop button → its own handler manages state
      if (e.target.closest('[data-ig-shop-button]')) return;
      this.closeAllDrawers();
    };
    document.addEventListener('click', this.onDocumentClick);

    // Escape closes any open drawer
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.closeAllDrawers();
    });
  }

  closeAllDrawers() {
    this.querySelectorAll('.ig-reel-card.is-chips-open').forEach((c) => {
      c.classList.remove('is-chips-open');
    });
  }

  hasOpenDrawer() {
    return !!this.querySelector('.ig-reel-card.is-chips-open');
  }

  /* ---------- GA4 / GTM tracking via dataLayer ---------- */

  setupTracking() {
    // Instagram link clicks (the card-level <a>)
    this.querySelectorAll('.ig-reel-card__link').forEach((link) => {
      link.addEventListener('click', () => {
        if (this.justDragged) return;
        this.trackEvent('open_instagram', { ig_reel_url: link.href });
      });
    });

    // Product chip clicks
    this.querySelectorAll('.ig-reel-card__chip').forEach((chip) => {
      chip.addEventListener('click', () => {
        this.trackEvent('view_product', {
          ig_reel_product_name: chip.dataset.igProductTitle || '',
          ig_reel_product_price: chip.dataset.igProductPrice || '',
          ig_reel_product_url: chip.href,
        });
      });
    });

    // Shop drawer opens
    this.querySelectorAll('[data-ig-shop-button]').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.trackEvent('open_shop_drawer');
      });
    });
  }

  trackEvent(action, extra = {}) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(
      Object.assign({ event: 'ig_reel_click', ig_reel_action: action }, extra)
    );
  }

  /* ---------- Mute / unmute (per-card state) ---------- */

  setupMuteButton() {
    this.querySelectorAll('[data-ig-mute-button]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (this.justDragged) return;

        const card = btn.closest('.ig-reel-card');
        if (!card) return;
        const video = card.querySelector('video');
        if (!video) return;

        video.muted = !video.muted;
        card.classList.toggle('is-unmuted', !video.muted);
        btn.setAttribute('aria-pressed', String(!video.muted));

        // If the card isn't currently playing, start it so the user can
        // immediately hear their choice take effect.
        if (video.paused || this.currentVideo !== video) {
          this.hoveredCard = card;
          this.playVideo(video, card);
        }
      });
    });
  }
}

if (!customElements.get('instagram-reels-slider')) {
  customElements.define('instagram-reels-slider', InstagramReelsSlider);
}
